import { useEffect, useState } from 'react'
import { eventGraphState, subscribeEventGraph } from '../lib/eventGraphState'
import { cameraRigState } from '../lib/cameraRigCompat'

// The editor writes eventGraphState on every keystroke; a poll avoids
// re-rendering with it. Poll + subscribe because EventGraph.jsx reassigns
// eventGraphState.nodes WITHOUT calling notifyEventGraphChange(), so the
// subscription alone would miss edits made in the editor.
const POLL_MS = 500

/**
 * useCameraRigState — 'ok' | 'no-rig' | 'no-graph' for the live event graph.
 *
 * Extracted from CameraRigNotice because the signal now has a second consumer:
 * Scene.jsx decides CAMERA OWNERSHIP with it (no rig in the blueprint → the
 * editor OrbitControls drives live mode instead of the blueprint rig — the
 * PlayCanvas/Unreal model: a scene camera when you author one, the editor
 * camera otherwise).
 *
 * Initialised SYNCHRONOUSLY from the current graph, not from a placeholder:
 * Scene mounts camera controllers off this value, and a wrong first render
 * would briefly hand the camera to the wrong owner.
 *
 * When `live` is false the answer is pinned to 'ok' (edit mode always has the
 * editor camera; the rig question is moot) and no polling runs.
 */
export function useCameraRigState(live) {
  const [state, setState] = useState(() =>
    live ? cameraRigState(eventGraphState.nodes) : 'ok')

  useEffect(() => {
    if (!live) { setState('ok'); return }
    const read = () => setState(cameraRigState(eventGraphState.nodes))
    read()
    const id = setInterval(read, POLL_MS)
    const unsub = subscribeEventGraph(read)
    return () => { clearInterval(id); unsub() }
  }, [live])

  return state
}
